import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import LegacyLandingDisplay from '@/components/dtcPartnerships/Landing/LegacyLandingDisplay';
import Loader from '@/components/Loader';
import StripeElementsProvider from '@/components/purchase/StripeElementsProvider';
import { isAuthPrompt, useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useDTCPartners } from '@/hooks/dtcLeadGenPartnership/useDTCPartners';
import { useDeviceState, usePricesState, useUserState } from '@/hooks/store';
import { setPartner } from '@/store/partner/actions';
import { User } from '@/store/user/types';
import { calmLogger } from '@/utils/calmLogger';
import { getCookie, removeCookie } from '@/utils/cookies';
import { putPartnerSubscribe } from '@/utils/endpoints';
import getParamAsString from '@/utils/getParamsAsString';
import isUSPricing from '@/utils/prices/isUSPricing';
import { hasSubscriptionConflictingWithB2BRedemption, userCanTrial } from '@/utils/subscriptions';

import { isPartner, partners } from '../shared/data';
import {
	IntegrationType,
	isSignupScreenKey,
	LandingDisplayProps,
	LandingProps,
	OnDTCScreenChange,
	PartnerData,
	PartnerDetails,
	SignupScreensKey,
} from '../shared/types';
import { signupScreens } from './signupScreens';
import { LoaderContainer } from './styles';

const Landing = ({ error, partnerSlug, referer, linkToken, rawPartner }: LandingProps) => {
	const prices = usePricesState();
	const user = useUserState();
	const { logEvent } = useAnalytics();
	const device = useDeviceState();
	const [errorData, setErrorData] = useState(error);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const defaultPartnerData = partners.default;
	const [partnerDetails, setPartnerDetails] = useState<PartnerData>({
		id: '',
		name: '',
		slug: rawPartner?.slug || '',
		requires_data_sharing: false,
		supports_eligibility_list: false,
		integration_type: IntegrationType.ACCESS_CODES,
		pageInfo: defaultPartnerData,
		should_show_request_access_flow: false,
	});
	const [activeScreen, setActiveScreen] = useState<SignupScreensKey | null>(null);
	const dispatch = useDispatch();
	const { setAuthPrompt } = useAuthPrompt();
	const searchParams = useSearchParams();
	const supportUrl = useState(() => getParamAsString(searchParams?.get('support_url')))[0];
	const redirectPath = useState(() => getParamAsString(searchParams?.get('redirect_path')))[0];
	const redirectURI = useState(() => getParamAsString(searchParams?.get('redirect_uri')))[0];
	const prefilledPartnerUserId = useState(() => {
		const ddId = getParamAsString(searchParams?.get('ddSubscriptionId'));
		if (ddId) {
			return ddId;
		}
		return getParamAsString(searchParams?.get('partner_user_id'));
	})[0];
	const prefilledEncryptedPartnerUserId = useState(() => {
		return getParamAsString(searchParams?.get('puid_token'));
	})[0];
	const dontClearCookies = useState(() =>
		// We (almost always) automatically log the user out when visiting this page, unless this query param is present
		getParamAsString(searchParams?.get('preserveSession')),
	)[0];
	const takeToAuthMode = useState(() => {
		const queryAuthMode = getParamAsString(searchParams?.get('authMode'));
		if (queryAuthMode && isAuthPrompt(queryAuthMode)) {
			return queryAuthMode;
		}
		return null;
	})[0];

	const { getDTCPartnerData } = useDTCPartners();
	const [userAccessCode, setUserAccessCode] = useState('');

	const onScreenChange: OnDTCScreenChange = (screen, mode) => {
		if (screen === 'account') {
			if (mode) {
				setAuthPrompt(mode);
			}
			setActiveScreen(screen);
		} else {
			setActiveScreen(screen);
		}
	};

	const redeem: LandingDisplayProps['redeem'] = async (_partnerDetails, uniqueId) => {
		setIsSubmitting(true);
		const { requires_data_sharing, id, pageInfo } = _partnerDetails;
		const dataSharingOptIn = requires_data_sharing;
		const redemptionMethod = (() => {
			if (linkToken) {
				return 'URL: calm-link-token';
			}
			if (prefilledPartnerUserId) {
				return 'URL: partner_user_id';
			}
			if (prefilledEncryptedPartnerUserId) {
				return 'URL: puid_token';
			}
			return 'Manual entry';
		})();
		const eventProps = {
			method: redemptionMethod,
			partner_id: id,
			partner_type: partnerDetails?.category?.toLowerCase() || null,
		};
		try {
			setErrorData(undefined);
			const partnerInfo = uniqueId
				? {
						partnerUserId: uniqueId.partner_user_id,
						puidToken: uniqueId.puid_token,
						partnerId: id,
						dataSharingOptIn,
						groupCode: uniqueId.group_code,
					}
				: { linkToken, dataSharingOptIn };
			logEvent({
				eventName: 'DTC Partnership Redemption : Submitted',
				eventProps,
			});
			if (partnerInfo.partnerUserId) setUserAccessCode(partnerInfo.partnerUserId);
			await putPartnerSubscribe(partnerInfo);
			setIsSubmitting(false);

			logEvent({
				eventName: 'DTC Partnership Redemption : Success',
				eventProps,
			});
			removeCookie('calm-link-token');
			if (redirectPath) {
				// redirect to a web-www redirect_path param
				// eslint-disable-next-line @next/next/no-location-assign-relative-destination
				window.location.href = `${window.location.origin}/${redirectPath}`;
			} else if (redirectURI) {
				// redirect to a url specified in redirect_uri param
				window.location.href = redirectURI;
			} else if (pageInfo.successRedirectUrl) {
				// redirect to a static url specified in the partner config
				window.location.href = pageInfo.successRedirectUrl;
			} else {
				onScreenChange('success');
			}
		} catch (err) {
			setIsSubmitting(false);
			if (err?.data.error.code !== 'payment_required') {
				logEvent({
					eventName: 'DTC Partnership Redemption : Failure',
					eventProps,
				});
				setErrorData(err.data);
			}
			if (err?.data.error.code === 'b2b_user_has_existing_subscription') {
				onScreenChange('sorry');
			} else if (err?.data.error.code === 'payment_required') {
				onScreenChange('payment');
			} else {
				setErrorData(err?.data.error);
			}
		}
	};

	async function onAuthSuccess(_user: User | null, nextPartnerDetails?: PartnerData): Promise<void> {
		const _partnerDetails = nextPartnerDetails ?? partnerDetails;
		if (_partnerDetails.offer_details?.trial_duration && !userCanTrial(_user)) {
			setErrorData({
				code: 'user_cannot_trial',
			});
			return onScreenChange('error');
		}
		if (_user?.subscription?.valid) {
			setErrorData({
				code: 'b2b_user_has_existing_subscription',
			});
			return onScreenChange('error');
		}
		if (linkToken) {
			await redeem(_partnerDetails);
		} else if (prefilledPartnerUserId) {
			await redeem(_partnerDetails, {
				partner_user_id: prefilledPartnerUserId,
			});
		} else if (prefilledEncryptedPartnerUserId) {
			await redeem(_partnerDetails, {
				puid_token: prefilledEncryptedPartnerUserId,
			});
		} else if (_partnerDetails) {
			onScreenChange('eligibility');
		} else {
			// This will almost certainly fail, but it'll take the user to the correct error screen
			await redeem(_partnerDetails);
		}
	}
	const _isDTCPartner = (_partnerDetails: PartnerDetails) => {
		return (
			Boolean(partnerDetails.offer_details) ||
			(_partnerDetails.category === 'BRAND_PARTNERSHIP' && _partnerDetails.name.toLowerCase() === 'adidas')
		);
	};

	async function fetchPartnerInfo(): Promise<void> {
		const userToken = getCookie('calm-user-token');
		try {
			if (!rawPartner) {
				setErrorData({
					code: 'b2b_partner_not_found',
				});
				return onScreenChange('error');
			}
			const partnerPageInfo = isPartner(partnerSlug) ? partners[partnerSlug] : partners.default;

			const partnerApiResponse = rawPartner;
			const dtcPartnerPageInfo = {
				...partnerPageInfo,
				...getDTCPartnerData(partnerApiResponse),
			};
			const _partnerData = {
				...partnerApiResponse,
				pageInfo: dtcPartnerPageInfo,
			};
			setPartnerDetails(_partnerData);
			dispatch(setPartner(_partnerData));

			const preserveUserSession = userToken && dontClearCookies;
			const isValidLinkToken = linkToken && linkToken !== 'null';
			const isValidRenewingUser = userToken && referer === 'renew' && isValidLinkToken;

			// Also for YouTube - remove when localized pricing is ready.
			// From here:
			if (!isUSPricing(prices, device) && rawPartner.slug.toLowerCase() === 'youtube-premium') {
				setErrorData({
					code: 'dtc_outside_eligible_region',
				});
				return onScreenChange('error');
			}
			// to here - AG

			if (preserveUserSession || isValidRenewingUser) {
				// we have to pass _partnerData here as the new values are not yet in scope of the onAuthSuccess function
				await onAuthSuccess(user, _partnerData);
				return;
			} else {
				removeCookie('calm-user-token');
				if (takeToAuthMode) {
					onScreenChange('account', takeToAuthMode);
				} else {
					onScreenChange('welcome');
				}
			}

			if (
				// user has active subscription take them directly to sorry
				userToken &&
				hasSubscriptionConflictingWithB2BRedemption(user?.subscription)
			) {
				onScreenChange('sorry');
				return;
			}
		} catch (err) {
			setErrorData(err?.data?.error);
		}
	}

	useEffect(
		() => {
			// remove the calm-link-token query param
			if (referer !== 'error') {
				const url = new URL(window.location.href);
				url.searchParams.delete('calm-link-token');
				window.history.replaceState({}, '', url.toString());
			}

			if (error) {
				onScreenChange('error');
			} else {
				fetchPartnerInfo().catch(error =>
					calmLogger.error('Error in dtc Landing fetchPartnerInfo', {}, error),
				);
			}
		}, // TODO [WEB-1595]: Fix this exhaustive-deps issue
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[error],
	);

	const Component =
		activeScreen && isSignupScreenKey(activeScreen) ? signupScreens[activeScreen].Component : null;
	if (activeScreen && Component) {
		return (
			<StripeElementsProvider isStripeNext>
				<LegacyLandingDisplay
					Component={Component}
					activeScreen={activeScreen}
					errorData={errorData}
					isSubmitting={isSubmitting}
					onAuthSuccess={onAuthSuccess}
					onScreenChange={onScreenChange}
					partnerDetails={partnerDetails}
					redeem={redeem}
					userAccessCode={userAccessCode}
					supportUrl={supportUrl}
					name={signupScreens[activeScreen].name}
					renderAsDtcPartner={_isDTCPartner(partnerDetails)}
				/>
			</StripeElementsProvider>
		);
	}
	return (
		<LoaderContainer>
			<Loader color="black" />
		</LoaderContainer>
	);
};

export default Landing;
